import { useEffect, useId, useRef } from 'react';
import type { ListingDetails } from 'app/types/listingDetails.type';
import { Button } from '../../../ui/Button';
import type { QualifierVariant } from './getQualifierVariant';
import * as styles from './contactFormQualifier.styles';

type ContactFormQualifierProps = {
  variant: QualifierVariant;
  listing: ListingDetails;
  onContinue: () => void;
};

const FALLBACK_INCOME_MESSAGE = 'This property has income requirements. You must meet the requirements to qualify.';

type VariantCopy = {
  heading: string;
  body: string;
  ctaLabel: string;
};

const COPY: Record<Exclude<QualifierVariant, 'incomeRestricted'>, VariantCopy> = {
  studentHousing: {
    heading: 'Student-only housing',
    body: 'This property is student-only housing. You must qualify to live here.',
    ctaLabel: 'Continue',
  },
  seniorHousing: {
    heading: 'Senior housing',
    body: 'You are about to contact a senior housing community. Verification of age requirements is required to live here.',
    ctaLabel: 'Continue',
  },
};

export function ContactFormQualifier({ variant, listing, onContinue }: ContactFormQualifierProps) {
  const ctaRef = useRef<HTMLButtonElement>(null);
  const headingId = useId();
  const descriptionId = useId();

  useEffect(() => {
    ctaRef.current?.focus();
  }, []);

  const content =
    variant === 'incomeRestricted'
      ? renderIncomeRestricted(listing, headingId, descriptionId)
      : renderSimple(variant, headingId, descriptionId);

  return (
    <div
      className={styles.scrim}
      style={styles.scrimInlineStyle}
      role="alertdialog"
      aria-labelledby={headingId}
      aria-describedby={descriptionId}
      aria-modal="true"
    >
      <div className={styles.dialog}>
        {content.body}
        <Button ref={ctaRef} type="button" shape="rectangle" tone="brand" emphasis="filled" onClick={onContinue}>
          {content.ctaLabel}
        </Button>
      </div>
    </div>
  );
}

function renderSimple(
  variant: Exclude<QualifierVariant, 'incomeRestricted'>,
  headingId: string,
  descriptionId: string,
) {
  const copy = COPY[variant];
  return {
    body: (
      <>
        <h2 id={headingId} className={styles.heading}>
          {copy.heading}
        </h2>
        <p id={descriptionId} className={styles.body}>
          {copy.body}
        </p>
      </>
    ),
    ctaLabel: copy.ctaLabel,
  };
}

function renderIncomeRestricted(listing: ListingDetails, headingId: string, descriptionId: string) {
  const message = listing.contact?.message || FALLBACK_INCOME_MESSAGE;
  const brackets = Object.entries(listing.contact?.restrictedIncomeByOccupants ?? {}).filter(
    ([, restriction]) => restriction !== '',
  );

  return {
    body: (
      <>
        <h2 id={headingId} className={styles.heading}>
          Income-restricted housing
        </h2>
        <p id={descriptionId} className={styles.body}>
          {message}
        </p>
        {brackets.length > 0 && (
          <ul className={styles.incomeList}>
            {brackets.map(([persons, restriction]) => (
              <li key={persons}>
                {Number(persons) === 1 ? '1 resident' : `${persons} residents`}, income below {restriction}
              </li>
            ))}
          </ul>
        )}
      </>
    ),
    ctaLabel: 'Yes, I qualify',
  };
}
